import { Injectable } from '@angular/core';
import { LoggerService } from '../../../core/services/logger.service';
import { UtilService } from '../../../core/services/util.service';
import { TicketCreationRequestControlService } from 'src/app/shared/services/ticket-creation-request-control.service';
import {
  CIFDatadogService,
  CloseTicketByEventProps,
} from 'src/app/shared/services/cif-datadog';
import { MediaType } from 'src/app/utils/media-type';
import { isDigital } from 'src/app/utils/cxone-utils';
import { LocalStorageUtils } from 'src/app/data/CXOne/util/LocalStorageUtils';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';
import { SessionStorageUtils } from 'src/app/data/CXOne/util/SessionStorageUtils';

export type Ticket = { id_interacao: string | null; id_ticket: string | null };

const templateDesligou: string = 'cb_nice_desligou';

@Injectable({
  providedIn: 'root',
})
export class DynamicsNpcService {
  private readonly RECALL_LIMIT = 20;
  private readonly RECALL_DELAY_MS = 2000;
  private messageHandler: (event: Event) => void;

  constructor(
    private logger: LoggerService,
    private utilService: UtilService,
    private ticketCreationRequestControlService: TicketCreationRequestControlService,
    private cifDatadogService: CIFDatadogService,
    private localStorageUtils: LocalStorageUtils,
    private jsonConverterUtils: JsonConverterUtils,
    private sessionStorageUtils: SessionStorageUtils,
  ) {
    this.messageHandler = this.DynamicsMessageHandler.bind(this);
    window.addEventListener('message', this.messageHandler);
  }

  async initialize(cifUrl: string): Promise<void> {
    this.logger.info(`DYNAMICS - inicializando CIF: ${cifUrl}`);
    this.utilService.getIframeEnvironment();
    const script = document.createElement('script');

    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');

    document.body.appendChild(script);

    return new Promise<void>((resolve, reject) => {
      const onCIFInitDone = async (event: Event) => {
        this.logger.info('DYNAMICS - CIF inicializado', event);
        try {
          this.logger.info('DYNAMICS - CIF Iframe setado para 400 pixels');

          await (window as any).Microsoft.CIFramework.setMode(1);

          await (window as any).Microsoft.CIFramework.addHandler(
            'onmodechanged',
            async (eventData: any) => {
              this.logger.info('DYNAMICS - CIF Mode Changed:', eventData);

              await (window as any).Microsoft.CIFramework.setMode(1);
            },
          );

          await (window as any).Microsoft.CIFramework.setWidth(400);
        } catch (error) {
          this.logger.error('DYNAMICS - Erro ao inicializar CIF', error);
          reject(error);
        } finally {
          resolve();
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };

      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async cifCreateSession(
    input_template: any,
    mediaType: MediaType | null,
    masterId: string,
  ): Promise<string> {
    let interactionId = null;
    try {
      interactionId = input_template['templateParameters'].id_interacao;
      this.cifDatadogService.openTicket.session.request({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
      });

      const success = await (
        window as any
      ).Microsoft.CIFramework.canCreateSession();
      if (success === true) {
        const sessionId = await (
          window as any
        ).Microsoft.CIFramework.createSession(input_template);
        this.logger.info('DYNAMICS - sessionId do createSession', sessionId);
        const sessionRelationship = {
          contactCenterInteractionId:
            input_template.templateParameters.conn_id != ''
              ? input_template.templateParameters.conn_id
              : input_template.templateParameters.id_interacao,
          dynamicsSessionId: sessionId,
          dynamicsinteractionId: input_template.templateParameters.id_ticket,
        };
        this.addSessionRelationship(sessionRelationship);

        this.cifDatadogService.openTicket.session.success({
          sessionId: sessionId,
          interactionId: interactionId,
          mediaType,
          masterId,
        });

        return sessionId;
      } else {
        this.logger.warn(
          'DYNAMICS - Não é possível criar uma sessão com o template fornecido.',
        );
        return '';
      }
    } catch (error: any) {
      this.logger.info('DYNAMICS - erro no callback do createSession', error);
      this.cifDatadogService.openTicket.session.fail({
        interactionId,
        template: input_template,
        mediaType,
        masterId,
        error,
      });
      throw error;
    }
  }

  public async cifCloseSession(
    idInteracao: string,
    sessionId: string,
    contactId: string,
    mediaType: MediaType | null,
  ): Promise<void> {
    try {
      await (window as any).Microsoft.CIFramework.raiseEvent('ControleQueda', {
        sessionId: sessionId,
      });

      var input = {
        templateName: templateDesligou,
        templateParameters: {},
      };
      await (window as any).Microsoft.CIFramework.notifyEvent(input);

      await (window as any).Microsoft.CIFramework.raiseEvent('ControleSessao', {
        sessionId: sessionId,
      });

      const closeEventLogDetails: CloseTicketByEventProps = {
        sessionId: sessionId,
        interactionId: idInteracao,
        masterId: contactId,
        mediaType,
      };

      this.cifDatadogService.closeTicket.start(
        'fecharSessao_controleSessao',
        closeEventLogDetails,
      );

      this.logger.info(`DYNAMICS - Sessão encerrada com sucesso: ${sessionId}`);
    } catch (error: any) {
      this.logger.error(error);
      this.cifDatadogService.closeTicket.fail('fecharSessao_controleSessao', {
        sessionId: sessionId,
        interactionId: idInteracao,
        error,
        mediaType,
        masterId: contactId,
      });
      throw error;
    }
  }

  public async cifCreateBlankTicket(
    id_interacao: string,
  ): Promise<string | null> {
    this.logger.info(
      `DYNAMICS - Criando ticket em branco para a interação: ${id_interacao}`,
    );

    const iduser = this.jsonConverterUtils
      .JsonParsed(await (window as any).Microsoft.CIFramework.getEnvironment())
      .userId.replace('{', '')
      .replace('}', '');

    const dataNewTicket = {
      cb_bo_clienteidentificado: false,
      cb_bo_negocioidentificado: false,
      cb_bo_produtoidentificado: false,
      cb_bo_excecaoprovedorcanal: true,
      cb_pl_tipo_atendimento: 121430002,
      statuscode: 803750009,
      'customerid_contact@odata.bind':
        '/contacts(00000000-0000-0000-0000-000000000001)',
      'cb_interacao_originadoraid@odata.bind': `/cb_interacaos(${id_interacao})`,
      'cb_agenteid@odata.bind': `/systemusers(${iduser})`,
    };

    const jsonTicket = JSON.stringify(dataNewTicket);

    // eslint-disable-next-line no-useless-catch
    try {
      const result = await (window as any).Microsoft.CIFramework.createRecord(
        'incident',
        jsonTicket,
      );
      // ID INTERAÇÃO
      const id_ticket = this.jsonConverterUtils.JsonParsed(result).id;
      return id_ticket;
    } catch (error) {
      throw error;
    }
  }

  public async cifCreateTicket(
    tableName: string,
    attachData: any,
    masterId: string,
    mediaType: MediaType,
    uJSON: unknown,
  ): Promise<Ticket> {
    let retorno: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };

    this.cifDatadogService.openTicket.initialData({
      attachData,
      masterId,
      mediaType,
    });

    if (isDigital(mediaType)) {
      const ticketHasRequest =
        this.ticketCreationRequestControlService.checkTicketRequest(masterId);

      if (ticketHasRequest === true) {
        this.logger.info(
          'DYNAMICS - Master ID já inserido na lista, ',
          masterId,
        );
        this.cifDatadogService.openTicket.request.duplicated({
          masterId: masterId,
          attachData: attachData,
          mediaType,
        });
        return retorno;
      }

      this.ticketCreationRequestControlService.saveTicketRequest(masterId);
      this.ticketCreationRequestControlService.clearUpLastTicketCreationRequest();
    }

    this.cifDatadogService.openTicket.request.create({
      attachData: uJSON,
      mediaType,
      masterId: masterId,
    });

    try {
      const result = await (window as any).Microsoft.CIFramework.createRecord(
        tableName,
        JSON.stringify(attachData),
      );

      this.cifDatadogService.openTicket.request.success({
        attachData,
        mediaType,
        masterId,
      });

      this.logger.info(`DYNAMICS - ID Interação criado: ${result}`);
      retorno.id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

      if (retorno.id_interacao == null) {
        if (attachData && attachData.connid) {
          const searchResult = await (
            window as any
          ).Microsoft.CIFramework.searchAndOpenRecords(
            'cb_interacao',
            `$filter=cb_id_externo eq '${attachData.connid}'&$select=cb_interacaoid&$expand=cb_cb_interacao_incident_interacao_originadoraid($select=incidentid)&$top=1`,
            true,
          );

          const interacao = this.jsonConverterUtils.JsonParsed(searchResult);

          if (interacao[0] != null) {
            retorno.id_interacao = interacao[0].cb_interacaoid;
            if (
              interacao[0]
                .cb_cb_interacao_incident_interacao_originadoraid[0] != null
            ) {
              retorno.id_ticket =
                interacao[0].cb_cb_interacao_incident_interacao_originadoraid[0].incidentid;
            }
            this.logger.info(
              `DYNAMICS - Ticket recuperada: ID Ticket: ${retorno.id_ticket}`,
            );
          }
        }
      }

      //recuperacao do ID do ticket
      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        retorno.id_ticket = await this.cifRetrieveRecord(
          retorno.id_interacao,
          tableName,
          mediaType,
          masterId,
        );
        this.logger.info(`DYNAMICS - Ticket recuperado: ${retorno.id_ticket}`);
      }
    } catch (error) {
      this.cifDatadogService.openTicket.request.fail({
        attachData,
        mediaType,
        masterId,
        error,
      });
      throw new Error(
        `Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
      );
    }
    return retorno;
  }

  private async cifRetrieveRecord(
    interactionId: string,
    tableName: string,
    mediaType: MediaType,
    masterId: string,
  ): Promise<string | null> {
    let idTicket = null;

    let finalizarProcessamento = false;

    this.cifDatadogService.openTicket.retrieve.start({
      interactionId,
      mediaType,
      masterId,
    });

    for (let attempt = 0; attempt < this.RECALL_LIMIT; attempt++) {
      if (finalizarProcessamento) break;

      await this.delay(this.RECALL_DELAY_MS);

      const options = `?$select=cb_protocolo_interacao&$expand=cb_cb_interacao_incident_interacao_originadoraid($select=incidentid)`;
      try {
        const result = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          options,
        );
        const interaction = this.jsonConverterUtils.JsonParsed(result);

        if (interaction.statuscode === 121430002) {
          this.logger.error(
            `Ocorreu um erro ao abrir o ticket atrelado a interação ${interactionId}.`,
          );
          finalizarProcessamento = true;
        }

        if (
          interaction &&
          interaction.cb_cb_interacao_incident_interacao_originadoraid &&
          interaction.cb_cb_interacao_incident_interacao_originadoraid[0] !=
            null
        ) {
          idTicket =
            interaction.cb_cb_interacao_incident_interacao_originadoraid[0]
              .incidentid;
          this.logger.info(
            `DYNAMICS - Ticket ID encontrado na tentativa ${attempt + 1}: ${idTicket}`,
          );

          this.cifDatadogService.openTicket.retrieve.success({
            interactionId,
            mediaType,
            masterId,
          });
          break;
        }
      } catch (error) {
        this.cifDatadogService.openTicket.retrieve.fail({
          interactionId,
          mediaType,
          masterId,
        });
        throw new Error(
          `Erro ao recuperar ticket na tentativa ${attempt + 1}: ${error instanceof Error ? error.message : error}`,
        );
      }
    }

    if (!idTicket) {
      this.cifDatadogService.openTicket.retrieve.fail({
        interactionId,
        mediaType,
        masterId,
      });
      this.logger.warn(
        `DYNAMICS - Limite de tentativas (${this.RECALL_LIMIT}) atingido ao recuperar o Ticket ID para a interação ${interactionId}`,
      );
    }

    return idTicket;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private addSessionRelationship(sessionData: any): void {
    this.localStorageUtils.safeSetItem(
      `sessionRelationship-${sessionData.contactCenterInteractionId}`,
      JSON.stringify(sessionData),
    );
  }

  private getSessionRelationship(
    ContactCenterInteractionId: string,
  ): any | null {
    const value = this.localStorageUtils.safeGetItem(
      `sessionRelationship-${ContactCenterInteractionId}`,
    );
    if (value) {
      this.logger.info('sessionRelationship recuperado', value);
      return this.jsonConverterUtils.JsonParsed(value);
    }
    this.logger.info('sessionRelationship vazio');
    return null;
  }

  private removeSessionRelationship(contactCenterInteractionId: string): void {
    this.localStorageUtils.safeRemoveItem(
      `sessionRelationship-${contactCenterInteractionId}`,
    );
  }

  public async ChangeSessionFocus(
    ContactCenterInteractionId: string,
  ): Promise<void> {
    this.logger.info(
      'NPC - Iniciando troca de focus',
      ContactCenterInteractionId,
    );
    const sessionRelationship = this.getSessionRelationship(
      ContactCenterInteractionId,
    );
    await (window as any).Microsoft.CIFramework.raiseEvent(
      'ControleFocusSessao',
      sessionRelationship?.dynamicsSessionId,
    );
    this.logger.info('NPC - Cif request controleFocusSessao finalizada');
  }

  public cifCloseSessionDigital(contactId: string, mediaType: MediaType) {
    const relationShip = this.getSessionRelationship(contactId);
    this.removeSessionRelationship(contactId);
    return this.cifCloseSession(
      relationShip.dynamicsSessionId,
      relationShip.dynamicsSessionId,
      contactId,
      mediaType,
    );
  }

  public DynamicsMessageHandler(e: any) {
    if (e.data.messageType === 'onSessionSwitched') {
      this.OnSessionSwitchHandlerFromWindow(e);
    }
  }

  public async OnSessionSwitchHandlerFromWindow(e: any) {
    this.logger.info(
      'Troca de sessão solicitada no dynamics via message',
      JSON.stringify(e),
    );

    const { sessionId, focused } = this.jsonConverterUtils.JsonParsed(
      e.data.messageData,
    );

    if (focused === false) {
      return;
    }

    const caseId = this.localStorageUtils.safeGetItem(sessionId);

    const shouldLetSwitchFocus =
      this.sessionStorageUtils.safeGetSessionItem(
        `sessionToBeClosed_${caseId}`,
      ) == sessionId;

    if (shouldLetSwitchFocus && focused) {
      this.logger.info(
        `Sessão ${sessionId} marcada para fechamento, ignorando troca de foco forçada.`,
      );
      return;
    }

    const contactCenterInteractionId =
      this.localStorageUtils.safeGetItem('interactionFocused') ?? '';
    const sessionRelationship = this.getSessionRelationship(
      contactCenterInteractionId,
    );

    const controleFocarSessao = sessionRelationship?.dynamicsSessionId;

    this.logger.info(
      'Solicitacao retorno a sessão do dynamics.',
      JSON.stringify(controleFocarSessao),
    );
    await (window as any).Microsoft.CIFramework.raiseEvent(
      'ControleFocusSessao',
      controleFocarSessao,
    );
  }

  public async OnSessionClosedHandlerFromWindow(e: any) {
    this.logger.info('Encerramento de sessão solicitada no dynamics.', e);
  }
}
